import type { ColDef, GetRowIdParams, GridOptions } from 'ag-grid-community'
import { themeQuartz } from 'ag-grid-community'

import {
  TABLE_HEADER_HEIGHTS,
  TABLE_ROW_HEIGHTS,
} from '@/components/lemmala-design-system/table/consts'
import { TABLE_LABELS } from '@/components/lemmala-design-system/table/labels'

/**
 * Creates the standardized AG Grid theme using the Quartz theme API.
 *
 * This utility extracts and generalizes the theme configuration from the original
 * gridOptions.ts, making it reusable across different table implementations.
 *
 * The theme uses semantic CSS variables that automatically support light/dark mode
 * and align with our Tailwind design tokens.
 */
export const createAgGridTheme = () => {
  return themeQuartz.withParams({
    // Borders - using semantic tokens for consistency
    borderColor: 'var(--border-default)',
    rowBorder: {
      color: 'var(--border-default)',
    },
    wrapperBorder: {
      color: 'var(--border-default)',
    },
    footerRowBorder: {
      width: '0px', // Disable footer borders as per Figma
    },
    wrapperBorderRadius: '6px', // Rounded corners to match design system

    // Hover and selection states
    rowHoverColor: 'var(--interactive-list-hover)',

    backgroundColor: 'var(--background-card)',

    // Header styling
    headerBackgroundColor: 'var(--background-card)',
    headerTextColor: 'var(--foreground-primary)',

    // Sizing - exact Figma measurements
    headerHeight: TABLE_HEADER_HEIGHTS.main,
    rowHeight: TABLE_ROW_HEIGHTS.double,

    // Future: add more sophisticated mappings as needed
  })
}

/**
 * Gets the default column definition that works with our theme and design system.
 *
 * This function provides sensible defaults for all AG Grid tables while allowing
 * customization for specific implementations.
 */
export const getDefaultColDef = <TRowData = unknown>(): ColDef<TRowData> => {
  return {
    sortable: true,
    resizable: true,
    floatingFilter: false, // Disable floating filters (using custom toolbar)
    suppressHeaderMenuButton: true, // Hide column menu button
    suppressHeaderFilterButton: true, // Hide filter button in header
    menuTabs: [], // No menu tabs
    suppressMovable: true, // Prevent column reordering
    suppressHeaderContextMenu: true, // Disable right-click menu
    filterParams: {
      suppressMiniFilter: true,
    },

    // Apply semantic styling using Tailwind classes
    headerClass:
      'bg-main-background-interactive-list-default border-main-border-default text-main-foreground-primary text-label-default',
    cellClass: 'flex items-center justify-start h-full px-4',

    // Show sort icons on hover only when unsorted; visible when sorted
    unSortIcon: true,
  } as ColDef<TRowData>
}

/**
 * Gets the base grid options that work with our theme and provide optimal performance.
 *
 * This function combines the theme with performance optimizations and sensible defaults
 * that match our application's requirements.
 */
export const getBaseGridOptions = <TRowData = unknown>(): GridOptions<TRowData> => {
  return {
    // Apply our custom theme
    theme: createAgGridTheme(),

    // Row model and data settings
    rowModelType: 'clientSide',
    getRowId: (params: GetRowIdParams<TRowData>) => {
      const data = params.data as unknown as { id?: string } | undefined
      return data?.id ?? ''
    },

    // Performance optimizations
    animateRows: false,
    rowBuffer: 20, // Lower buffer reduces DOM overhead
    debounceVerticalScrollbar: false,
    suppressColumnVirtualisation: false,
    cacheQuickFilter: true,
    suppressAnimationFrame: true, // Reduce animation scheduling overhead
    suppressColumnMoveAnimation: true, // No column move animations
    maintainColumnOrder: true, // Preserve column order across columnDefs updates

    // Scrolling behavior
    suppressHorizontalScroll: false,
    alwaysShowHorizontalScroll: false,
    alwaysShowVerticalScroll: false,
    suppressScrollOnNewData: true,

    // Disable enterprise features not needed
    sideBar: false,

    // Row styling for interactions
    rowClass: 'cursor-pointer group',

    // Status bar disabled (custom results count in toolbar)
    statusBar: {
      statusPanels: [],
    },

    // Icons: hide header filter/funnel icon entirely
    icons: {
      filter: '',
      filterActive: '',
    },

    // Filter settings
    includeHiddenColumnsInQuickFilter: false,

    // Localization
    localeText: {
      noRowsToShow: TABLE_LABELS.noResultsToDisplay,
    },

    // Focus and context menu settings
    suppressCellFocus: true, // Only row selection, no cell focus
    suppressContextMenu: true, // Disable right-click menu

    // Portal popups to the body so the grid's own overflow:hidden root can't clip them.
    popupParent: document.body,
  } as GridOptions<TRowData>
}

/**
 * Merges user-provided grid options with our base options.
 *
 * This allows table implementations to override specific settings while
 * maintaining our theme and performance defaults.
 */
export const mergeGridOptions = <TRowData = unknown>(
  userOptions?: Partial<GridOptions<TRowData>>,
): GridOptions<TRowData> => {
  const baseOptions = getBaseGridOptions<TRowData>()

  if (!userOptions) {
    return baseOptions
  }

  return {
    ...(baseOptions as GridOptions<TRowData>),
    ...(userOptions as Partial<GridOptions<TRowData>>),
    // Ensure theme is preserved unless explicitly overridden
    theme: (userOptions.theme as GridOptions<TRowData>['theme']) ?? baseOptions.theme,
    // Merge localeText instead of replacing
    localeText: {
      ...baseOptions.localeText,
      ...userOptions.localeText,
    },
  }
}
